/**
 * Treats notification recipients as one shared setting, even though the
 * orchestrator stores them per schedule.
 *
 * There is no settings endpoint on the service — `/settings`, `/config` and
 * `/notification-settings` all 404 — and the notifier reads addresses off the
 * schedule record itself. So "the shared list" is not stored anywhere; it is
 * derived: whichever exact list the most schedules already agree on. Saving in
 * the settings panel writes that list back to every schedule that isn't
 * deliberately overriding it.
 *
 * The upside of deriving rather than storing is that it cannot fall out of
 * sync with reality. There is no hidden preference claiming one thing while
 * the schedules say another.
 */
import type { PipelineSchedule } from '../services/pipelineOrchestrator';
import { NEW_SCHEDULE_DEFAULTS } from './scheduleDefaults';

/** Order- and case-insensitive identity for a recipient list. */
export function recipientKey(emails: readonly string[]): string {
  return [...emails]
    .map(e => e.trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join(',');
}

/** True when two lists name the same people, whatever the order or casing. */
export function sameRecipients(a: readonly string[], b: readonly string[]): boolean {
  return recipientKey(a) === recipientKey(b);
}

/**
 * The list most schedules already use.
 *
 * Ties break toward the longer list, then alphabetically, so the result is
 * stable across reloads rather than depending on fetch order.
 */
export function sharedRecipients(schedules: readonly PipelineSchedule[]): string[] {
  const groups = new Map<string, { emails: string[]; count: number }>();

  for (const schedule of schedules) {
    const emails = schedule.notification_emails ?? [];
    if (emails.length === 0) continue;
    const key = recipientKey(emails);
    const existing = groups.get(key);
    if (existing) existing.count++;
    else groups.set(key, { emails: [...emails], count: 1 });
  }

  if (groups.size === 0) return [...NEW_SCHEDULE_DEFAULTS.notificationEmails];

  const best = [...groups.values()].sort(
    (a, b) =>
      b.count - a.count ||
      b.emails.length - a.emails.length ||
      recipientKey(a.emails).localeCompare(recipientKey(b.emails))
  )[0];

  return best ? [...best.emails] : [...NEW_SCHEDULE_DEFAULTS.notificationEmails];
}

/** Schedules whose recipients differ from the shared list. */
export function schedulesOverriding(
  schedules: readonly PipelineSchedule[],
  shared: readonly string[]
): PipelineSchedule[] {
  return schedules.filter(s => !sameRecipients(s.notification_emails ?? [], shared));
}
